import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Chip,
  Stack,
} from '@mui/material';
import { RefreshCw, AlertTriangle, CheckCircle2, Download } from 'lucide-react';
import { authenticatedFetch } from '@diggens/api-client';

interface DemoControlsTabProps {
  tenantId: string;
}

interface VerificationResult {
  check: string;
  status: 'pass' | 'fail' | 'warning';
  message: string;
}

export function DemoControlsTab({ tenantId }: DemoControlsTabProps) {
  const [selectedPeriod, setSelectedPeriod] = useState('Q4 2024');
  const [loading, setLoading] = useState(false);
  const [verificationResults, setVerificationResults] = useState<VerificationResult[] | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    mode: 'quick' | 'period' | 'full' | null;
    title: string;
    message: string;
  }>({
    open: false,
    mode: null,
    title: '',
    message: '',
  });
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);

  const periods = ['Q3 2024', 'Q4 2024', 'Q1 2025'];

  const handleQuickReset = () => {
    setConfirmDialog({
      open: true,
      mode: 'quick',
      title: 'Quick Reset',
      message: 'This will delete submissions, metrics, comments, and conversations. Companies, users, and funds will be kept. Continue?',
    });
  };

  const handlePeriodReset = () => {
    setConfirmDialog({
      open: true,
      mode: 'period',
      title: `Reset ${selectedPeriod}`,
      message: `This will delete all submissions and metrics for ${selectedPeriod}. Other periods will be kept. Continue?`,
    });
  };

  const handleFullReset = () => {
    setConfirmDialog({
      open: true,
      mode: 'full',
      title: 'Full Reset',
      message: 'This will delete ALL tenant data and reload from CSV files. This is a complete refresh to baseline demo state. Continue?',
    });
  };

  const handleConfirmReset = async () => {
    const mode = confirmDialog.mode;
    setConfirmDialog({ open: false, mode: null, title: '', message: '' });

    if (!mode) return;

    setLoading(true);
    setAlert(null);

    try {
      const response = await authenticatedFetch('/admin/demo-reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tenantId,
          mode,
          period: mode === 'period' ? selectedPeriod : undefined,
        }),
      });

      if (!response.ok) {
        throw new Error('Reset failed');
      }

      // const result = await response.json();
      setAlert({
        type: 'success',
        message: `${mode === 'quick' ? 'Quick' : mode === 'period' ? 'Period' : 'Full'} reset completed successfully`,
      });

      if (mode === 'full') {
        setVerificationResults(null);
      }
    } catch (error) {
      setAlert({
        type: 'error',
        message: error instanceof Error ? error.message : 'Reset failed',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    setLoading(true);
    setAlert(null);
    setVerificationResults(null);

    try {
      const response = await authenticatedFetch(`/admin/demo-verify?tenantId=${tenantId}`);

      if (!response.ok) {
        throw new Error('Verification failed');
      }

      const result = await response.json();
      setVerificationResults(result.checks);

      const failed = result.checks.filter((r: VerificationResult) => r.status === 'fail').length;
      const warnings = result.checks.filter((r: VerificationResult) => r.status === 'warning').length;

      if (failed > 0) {
        setAlert({
          type: 'error',
          message: `Verification failed: ${failed} checks failed, ${warnings} warnings`,
        });
      } else if (warnings > 0) {
        setAlert({
          type: 'info',
          message: `Verification passed with ${warnings} warnings`,
        });
      } else {
        setAlert({
          type: 'success',
          message: 'All verification checks passed',
        });
      }
    } catch (error) {
      setAlert({
        type: 'error',
        message: error instanceof Error ? error.message : 'Verification failed',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setLoading(true);
    setAlert(null);

    try {
      const response = await authenticatedFetch('/admin/demo-export', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tenantId }),
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      setAlert({
        type: 'success',
        message: 'Export completed successfully. Check test-data/demo-data/ directory.',
      });
    } catch (error) {
      setAlert({
        type: 'error',
        message: error instanceof Error ? error.message : 'Export failed',
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pass':
        return <CheckCircle2 size={16} color="green" />;
      case 'fail':
        return <AlertTriangle size={16} color="red" />;
      case 'warning':
        return <AlertTriangle size={16} color="orange" />;
      default:
        return null;
    }
  };

  return (
    <Box>
      {alert && (
        <Alert severity={alert.type} sx={{ mb: 3 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <RefreshCw size={20} />
            Reset Options
          </Typography>

          <Stack spacing={2}>
            <Box>
              <Button variant="outlined" onClick={handleQuickReset} disabled={loading} fullWidth sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
                <Box sx={{ textAlign: 'left', width: '100%' }}>
                  <Typography variant="subtitle2">Quick Reset</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Clear submissions, metrics, comments. Keep structure.
                  </Typography>
                </Box>
              </Button>
            </Box>

            <Box>
              <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                <InputLabel>Period</InputLabel>
                <Select value={selectedPeriod} onChange={(e) => setSelectedPeriod(e.target.value)} label="Period">
                  {periods.map((period) => (
                    <MenuItem key={period} value={period}>
                      {period}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="outlined" onClick={handlePeriodReset} disabled={loading} fullWidth sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
                <Box sx={{ textAlign: 'left', width: '100%' }}>
                  <Typography variant="subtitle2">Reset Period</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Clear data for selected period only.
                  </Typography>
                </Box>
              </Button>
            </Box>

            <Box>
              <Button variant="outlined" color="error" onClick={handleFullReset} disabled={loading} fullWidth sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
                <Box sx={{ textAlign: 'left', width: '100%' }}>
                  <Typography variant="subtitle2">Full Reset</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Delete all data and reload from CSV files.
                  </Typography>
                </Box>
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle2 size={20} />
            Verification
          </Typography>

          <Button variant="contained" onClick={handleVerify} disabled={loading} fullWidth startIcon={<RefreshCw />}>
            {loading ? 'Verifying...' : 'Verify Demo Data'}
          </Button>

          {verificationResults && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Verification Results
              </Typography>
              <Stack spacing={1}>
                {verificationResults.map((result, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1,
                      bgcolor: 'background.default',
                      borderRadius: 1,
                    }}
                  >
                    {getStatusIcon(result.status)}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2">{result.check}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {result.message}
                      </Typography>
                    </Box>
                    <Chip label={result.status} size="small" color={result.status === 'pass' ? 'success' : result.status === 'fail' ? 'error' : 'warning'} />
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Download size={20} />
            Export
          </Typography>

          <Button variant="outlined" onClick={handleExport} disabled={loading} fullWidth startIcon={<Download />}>
            {loading ? 'Exporting...' : 'Export Current State to CSV'}
          </Button>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Exports current tenant data to test-data/demo-data/ directory
          </Typography>
        </CardContent>
      </Card>

      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>Cancel</Button>
          <Button onClick={handleConfirmReset} color="error" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {loading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
}
